import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";

import type { SourceDetail } from "api/schemas/sourcesApiSchemas";
import { sourcesApiSlice } from "api/sourcesApi.slice";
import { AddItemIcon, CrossCircledIcon } from "common/components";
import { Autocomplete } from "common/components/form/Autocomplete/Autocomplete";
import { AutocompleteOptionTag } from "common/components/form/Autocomplete/AutocompleteOptionTag";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import * as Styled from "domains/search/components/SourcesFilter/SourcesFilter.styled";
import { searchSlice } from "domains/search/search.slice";

export const SourcesFilter = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const selectedSources = useSelector((state) => state.search.sources);

  const [autocompleteInput, setAutocompleteInput] = useState("");
  const [debouncedAutocompleteInput] = useDebounce(autocompleteInput, 500);

  const { addSource, removeSource } = searchSlice.actions;

  const {
    data: paginatedSources,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = sourcesApiSlice.endpoints.getSources.useInfiniteQuery({
    searchQuery: debouncedAutocompleteInput,
    filters: {
      status: true,
      type: null,
      publicationType: null,
      contentType: null,
      license: null,
      countries: [],
      regions: [],
      greaterRegions: [],
    },
  });

  const sources = useMemo(() => paginatedSources?.pages.flat() ?? [], [paginatedSources?.pages]);

  const addSourceSelectOption = (source: SourceDetail) => dispatch(addSource({ source }));
  const removeSourceSelectOption = (source: SourceDetail) => dispatch(removeSource({ removedSource: source }));

  const onSourceSelectOptionClick = (source: SourceDetail) => {
    if (selectedSources.find((selectedOption) => selectedOption.id === source.id)) {
      removeSourceSelectOption(source);
      return;
    }
    addSourceSelectOption(source);
  };

  const loadNextPage = async () => {
    if (hasNextPage && !isFetchingNextPage) await fetchNextPage();
  };

  const checkIsCurrentSourceSelected = (currentSource: SourceDetail) =>
    selectedSources.some((selectedOption) => selectedOption.id === currentSource.id);

  return (
    <Styled.SourceAutocompleteContainer>
      <Styled.Label htmlFor="source-name-multi-select">{translate("app.moderate.sources.title")}</Styled.Label>
      {selectedSources.length > 0 && (
        <Styled.SelectedSourcesContainer>
          {selectedSources.map((item) => (
            <AutocompleteOptionTag
              key={`selected-tag-${item.id}`}
              label={item.name}
              onClick={() => removeSourceSelectOption(item)}
              isSelected
              labelFontSize="12px"
            />
          ))}
        </Styled.SelectedSourcesContainer>
      )}
      <Autocomplete
        id="source-name-multi-select"
        value={autocompleteInput}
        placeholder={translate("app.search.filter.sources.placeholder")}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        options={sources.map((source) => ({
          ...(checkIsCurrentSourceSelected(source)
            ? { icon: <CrossCircledIcon />, iconLabel: translate("app.search.filter.sources.remove") }
            : { icon: <AddItemIcon />, iconLabel: translate("app.search.filter.sources.remove") }),
          image: source.favicon,
          id: source.id,
          name: source.name,
          onClick: () => onSourceSelectOptionClick(source),
        }))}
        resultsContainerHeight={250}
        onChange={setAutocompleteInput}
        onClear={() => setAutocompleteInput("")}
        onEndReached={loadNextPage}
      />
    </Styled.SourceAutocompleteContainer>
  );
};
